import { DateTime } from 'luxon';
import { Box, Typography } from '@mui/material';
import { orange, red, yellow } from '@mui/material/colors';
import { DataTable } from '../../components/DataTable/DataTable';
import { TableColumn } from '../../components/DataTable/TableColumn';
import { TextColumn } from '../../components/DataTable/TextColumn';
import { BoolColumn } from '../../components/DataTable/BoolColumn';
import { Pagination } from '../../interfaces/Pagination';
import { ExpiryReport } from '../../interfaces/Report/ExpiryReport/ExpiryReport';
import { sharedColors } from '../../utils/Style';

const expiryLegend = [
  { colour: red[100], label: '3 aydan az (süresi geçenler dahil)' },
  { colour: orange[100], label: '3–6 ay' },
  { colour: yellow[100], label: '6 ay–1 yıl' },
  { colour: sharedColors.gray1, label: '1 yıl ve üzeri veya SKT yok' },
];

const columns: TableColumn<ExpiryReport>[] = [
  {
    id: 'product-name',
    name: 'Ürün Adı',
    accessor: (row) => <TextColumn value={row.productName} />,
  },
  {
    id: 'option-name',
    name: 'Varyant',
    accessor: (row) => <TextColumn value={row.optionName ?? '—'} />,
  },
  {
    id: 'sku',
    name: 'SKU',
    accessor: (row) => <TextColumn value={row.sku ?? '—'} />,
  },
  {
    id: 'barcode',
    name: 'Barkod',
    accessor: (row) => <TextColumn value={row.barcode ?? '—'} />,
  },
  {
    id: 'quantity',
    name: 'Stok Miktarı',
    accessor: (row) => <TextColumn value={row.quantity.toString()} />,
  },
  {
    id: 'status',
    name: 'Durum',
    accessor: (row) => <BoolColumn value={row.isActive} />,
  },
  {
    id: 'expiry-date',
    name: 'SKT',
    accessor: (row) => (
      <TextColumn
        value={
          row.expiryDate
            ? DateTime.fromISO(row.expiryDate).toFormat('dd.MM.yyyy')
            : '—'
        }
      />
    ),
  },
];

type ExpiryReportTableProps = {
  rows: ExpiryReport[];
  totalCount: number;
  pagination: Pagination;
  onPagination: (pagination: Pagination) => void;
};

export const ExpiryReportTable = (props: ExpiryReportTableProps) => {
  const today = DateTime.local().startOf('day');
  const threeMonths = today.plus({ months: 3 }).toMillis();
  const sixMonths = today.plus({ months: 6 }).toMillis();
  const oneYear = today.plus({ years: 1 }).toMillis();

  const rowColorizer = (row: ExpiryReport): string => {
    if (!row.expiryDate) return '';
    const expiry = DateTime.fromISO(row.expiryDate);
    if (!expiry.isValid) return '';

    const expiryTime = expiry.toMillis();
    if (expiryTime < threeMonths) return red[100];
    if (expiryTime < sixMonths) return orange[100];
    if (expiryTime < oneYear) return yellow[100];
    return '';
  };

  return (
    <>
      <DataTable
        columns={columns}
        rows={props.rows}
        totalCount={props.totalCount}
        limit={props.pagination.pageSize}
        setLimit={(pageSize) => props.onPagination({ page: 0, pageSize })}
        page={props.pagination.page}
        setPage={(page) => props.onPagination({ ...props.pagination, page })}
        rowColorizer={rowColorizer}
        onClick={(row) =>
          window.open(
            `/products/catalog/${row.productID}`,
            '_blank',
            'noopener',
          )
        }
      />
      <Box
        component='ul'
        aria-label='SKT renk açıklaması'
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          m: 0,
          mt: 1.5,
          p: 0,
          listStyle: 'none',
        }}
      >
        {expiryLegend.map(({ colour, label }) => (
          <Box
            component='li'
            key={label}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
          >
            <Box
              component='span'
              aria-hidden='true'
              sx={{
                width: 16,
                height: 16,
                flexShrink: 0,
                borderRadius: 0.5,
                border: 1,
                borderColor: 'divider',
                backgroundColor: colour,
              }}
            />
            <Typography variant='body2' color='text.secondary'>
              {label}
            </Typography>
          </Box>
        ))}
      </Box>
    </>
  );
};
